import { ReactNode } from 'react';
import { Shield, FileText, AlertTriangle, Ban, Scale, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';

interface TermsDialogProps {
  children: ReactNode;
}

export function TermsDialog({ children }: TermsDialogProps) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[85vh] p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-white/10">
          <DialogTitle className="text-2xl">Terms of Service</DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </DialogHeader>

        <ScrollArea className="h-[calc(85vh-120px)] px-6">
          <div className="space-y-6 py-4">
            {/* Introduction */}
            <div className="space-y-3">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-primary/10 flex-shrink-0">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">Welcome to NodeCast</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    By using NodeCast, you agree to these terms. Please read them carefully.
                    NodeCast is a decentralized music sharing platform built on the Nostr protocol.
                  </p>
                </div>
              </div>
            </div>

            {/* Third-Party Hosting */}
            <div className="space-y-3">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-sky-500/10 flex-shrink-0">
                  <Shield className="h-5 w-5 text-sky-500" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">Third-Party Hosting</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    <strong className="text-foreground">NodeCast does not host music files.</strong> All audio files are stored on third-party Blossom servers,
                    not on NodeCast's infrastructure. Currently, NodeCast uses the{' '}
                    <a
                      href="https://primal.net"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      primal.net
                    </a>
                    {' '}Blossom server, but may utilize other Blossom servers in the future.
                  </p>
                  <p className="text-xs text-muted-foreground italic">
                    Blossom servers are decentralized file storage providers that operate independently from NodeCast.
                  </p>
                </div>
              </div>
            </div>

            {/* Copyright Compliance */}
            <div className="space-y-3">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-purple-500/10 flex-shrink-0">
                  <Scale className="h-5 w-5 text-purple-500" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">Copyright & Ownership</h3>
                  <div className="space-y-2 text-sm text-muted-foreground leading-relaxed">
                    <p>
                      <strong className="text-foreground">You must own the rights to content you upload.</strong> Do not submit copyrighted
                      material unless you hold the copyright or have explicit permission from the copyright holder.
                    </p>
                    <p>
                      NodeCast reserves the right to filter out copyrighted content and content that violates these terms.
                      Violations may result in blacklisting from the platform.
                    </p>
                    <div className="mt-3 p-3 bg-purple-500/5 border border-purple-500/20 rounded-lg">
                      <p className="text-xs">
                        <strong className="text-purple-400">For Copyright Holders:</strong> If you believe your copyrighted
                        work has been uploaded without authorization, please contact the Blossom server provider directly.
                        For content hosted on primal.net, reach out to their support team. NodeCast facilitates metadata only
                        and does not store the actual files.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Anti-Spam Policy */}
            <div className="space-y-3">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-orange-500/10 flex-shrink-0">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">Anti-Spam Policy</h3>
                  <div className="space-y-2 text-sm text-muted-foreground leading-relaxed">
                    <p>
                      <strong className="text-foreground">Spamming is strictly prohibited.</strong> Users who engage in
                      spam activities will be blacklisted from the platform.
                    </p>
                    <p>
                      Consistent abuse of the platform, including repeated spam violations, will result in IP-based blacklisting
                      to protect the community and maintain platform integrity.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Content Policy */}
            <div className="space-y-3">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-red-500/10 flex-shrink-0">
                  <Ban className="h-5 w-5 text-red-500" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">Content Policy</h3>
                  <div className="space-y-2 text-sm text-muted-foreground leading-relaxed">
                    <p>
                      <strong className="text-foreground">Illegal content is strictly prohibited.</strong> Do not upload,
                      share, or distribute any content that violates local, national, or international laws.
                    </p>
                    <p>
                      <strong className="text-foreground">NodeCast is a music platform.</strong> All published events must be
                      music-related. Non-music content will be filtered out, and users who repeatedly publish non-music
                      events will be blacklisted.
                    </p>
                    <div className="mt-3 p-3 bg-red-500/5 border border-red-500/20 rounded-lg">
                      <p className="text-xs">
                        <strong className="text-red-400">Zero Tolerance:</strong> Illegal content, including but not limited to
                        illegal pornography, content promoting violence, hate speech, or content violating intellectual property
                        rights, will result in immediate and permanent blacklisting.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Enforcement */}
            <div className="space-y-3">
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Enforcement & Moderation</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  NodeCast employs automated and manual moderation to enforce these terms. We reserve the right to:
                </p>
                <ul className="list-disc list-inside space-y-1.5 text-sm text-muted-foreground ml-4">
                  <li>Filter or remove content that violates these terms</li>
                  <li>Blacklist users who repeatedly violate these terms</li>
                  <li>Implement IP-based restrictions for consistent abusers</li>
                  <li>Update these terms at any time to protect the community</li>
                </ul>
              </div>
            </div>

            {/* Contact */}
            <div className="space-y-3 pb-4">
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Questions?</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  If you have questions about these terms, please reach out via Nostr or through our community channels.
                  By continuing to use NodeCast, you acknowledge that you have read, understood, and agree to these Terms of Service.
                </p>
              </div>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
